import {
    Box,
    Card,
    Stack,
    Table,
    TableBody,
    TableContainer,
    useTheme
} from '@mui/material';
import {
    TableCell,
    TableFooter,
    TableFooterCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from '@/components/shared/styles/Table';
import { useTranslation } from 'react-i18next';
import CardHeader from '@/components/shared/CardHeader';
import { TableCard } from '@/components/shared/Card';
import Chip from '@/components/shared/Chip';
import { SMBold, XSMedium } from '@/typography/Typography';
import { ReconciliationTransaction } from '@/lib/slices/paymentsSlice';
import { useFormatting } from '@/utils/formatting';
import { UserContext, useUser } from '@/contexts/user/UserContext';
import { useContext } from 'react';
import { TipsTransaction } from '@/services/PaymentsServiceV2';

interface ReconciliationTableProps {
    label: string;
    items: ReconciliationTransaction[] | TipsTransaction[];
    totalSum?: string;
    totalAmount?: string;
    feeTotal?: string;
    chipText?: string;
    showFees?: boolean;
    showDistributedAt?: boolean;
}

export const ReconciliationTable = ({
    label,
    items,
    chipText,
    totalSum,
    feeTotal,
    totalAmount,
    showFees = true,
    showDistributedAt = false
}: ReconciliationTableProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { formatDate, getBalanceLabel } = useFormatting();

    const { company } = useContext(UserContext);

    return (
        <TableCard>
            <CardHeader
                label={
                    <Stack direction={'row'} alignItems={'center'}>
                        {label}{' '}
                        <Box pl={1}>
                            <Chip
                                icon={<span></span>}
                                label={
                                    <XSMedium
                                        color={theme.palette.success.main}
                                    >
                                        {chipText}
                                    </XSMedium>
                                }
                            ></Chip>
                        </Box>
                    </Stack>
                }
            ></CardHeader>
            <TableContainer>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell width={'15%'}>
                                {t('date')}
                            </TableHeaderCell>
                            {showDistributedAt && (
                                <TableHeaderCell width={'15%'}>
                                    {t('distributed_at')}
                                </TableHeaderCell>
                            )}
                            <TableHeaderCell width={'30%'}>
                                {t('description')}
                            </TableHeaderCell>
                            {showFees && (
                                <TableHeaderCell width={'20%'} align="right">
                                    {t('fee')}
                                </TableHeaderCell>
                            )}

                            {!showFees && (
                                <TableHeaderCell
                                    width={'20%'}
                                ></TableHeaderCell>
                            )}
                            <TableHeaderCell width={'15%'} align="right">
                                {t('amount')}
                            </TableHeaderCell>
                            <TableHeaderCell width={'15%'} align="right">
                                {t('Sum')}
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((item, index) => {
                            return (
                                <TableRow key={index}>
                                    <TableCell>
                                        {formatDate(new Date(item.date))}
                                    </TableCell>
                                    {showDistributedAt && (
                                        <TableCell>
                                            {formatDate(
                                                new Date(
                                                    (
                                                        item as TipsTransaction
                                                    ).createdAt
                                                )
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell>{item.description}</TableCell>
                                    {showFees && (
                                        <TableCell align="right">
                                            {getBalanceLabel(
                                                (
                                                    item as ReconciliationTransaction
                                                ).withdrawalFee,
                                                company.currency
                                            )}
                                        </TableCell>
                                    )}
                                    {!showFees && <TableCell></TableCell>}
                                    <TableCell align="right">
                                        {getBalanceLabel(
                                            item.amount,
                                            company.currency
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {getBalanceLabel(
                                            item.sum,
                                            company.currency
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>

                    <TableFooter>
                        <TableRow>
                            <TableFooterCell>
                                <SMBold>{t('total')}</SMBold>
                            </TableFooterCell>
                            <TableFooterCell></TableFooterCell>
                            {showDistributedAt && (
                                <TableFooterCell></TableFooterCell>
                            )}
                            {showFees && (
                                <TableFooterCell align="right">
                                    <SMBold>{feeTotal}</SMBold>
                                </TableFooterCell>
                            )}
                            {!showFees && <TableFooterCell></TableFooterCell>}
                            <TableFooterCell align="right">
                                <SMBold>{totalAmount}</SMBold>
                            </TableFooterCell>
                            <TableFooterCell align="right">
                                <SMBold>{totalSum}</SMBold>
                            </TableFooterCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </TableCard>
    );
};
